import { useEffect, useMemo, useRef, useState } from "react";
import { formatPriceText, formatPriceWithPrefix, toNumericPriceValue } from "../../lib/currency";
import { getStrapiMediaAttributes } from "../../lib/strapi";
import { getIconFallbackLabel, getUiText } from "../../lib/uiText";
import { useGlobal } from "../../context/GlobalContext";
import useScrollReveal from "../../hooks/useScrollReveal";
import iconFlechaIzq from "../../assets/icons/icon_flecha_izq.svg";
import iconFlechaDer from "../../assets/icons/icon_flecha_der.svg";
import styles from "./Tipologias.module.css";

const getSlugFromUrl = (url) => {
  if (!url) return null;
  const normalizedUrl = String(url).trim();
  if (!normalizedUrl) return null;

  const withoutOrigin = normalizedUrl.replace(/^https?:\/\/[^/]+/i, "");
  const pathOnly = withoutOrigin.split("?")[0].split("#")[0];
  const cleaned = pathOnly.replace(/^\/+|\/+$/g, "");

  return cleaned || null;
};

const Tipologias = ({ data }) => {
  const { companyInfo, priceMap, locale } = useGlobal();
  const cards = data?.cards || [];
  const mostrarPrecio = data?.mostrarPrecio !== false;
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const resumeTimeoutRef = useRef(null);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchEndXRef = useRef(0);
  const touchEndYRef = useRef(0);
  const [sectionRef, isVisible] = useScrollReveal({ threshold: 0.2 });

  const total = cards.length;
  const rawAutoplaySpeed = Number(data?.autoplaySpeed || 0);
  const autoplaySpeed = useMemo(() => {
    if (!Number.isFinite(rawAutoplaySpeed) || rawAutoplaySpeed <= 0) return 0;
    return rawAutoplaySpeed <= 60 ? rawAutoplaySpeed * 1000 : rawAutoplaySpeed;
  }, [rawAutoplaySpeed]);
  const showNavigation = total > 3;
  const useArrowNavigation = showNavigation && data?.mostrarFlechas !== false;
  const showDots = showNavigation && !useArrowNavigation;

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 1024);

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);

    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  useEffect(() => {
    if (total <= 1 || autoplaySpeed <= 0 || isPaused) return undefined;

    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % total);
    }, autoplaySpeed);

    return () => clearInterval(interval);
  }, [total, autoplaySpeed, isPaused]);

  useEffect(() => () => {
    clearTimeout(resumeTimeoutRef.current);
  }, []);

  const withPause = (fn) => {
    setIsPaused(true);
    fn();
    clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 4500);
  };

  const goPrev = () => withPause(() => setCurrent((prev) => (prev - 1 + total) % total));
  const goNext = () => withPause(() => setCurrent((prev) => (prev + 1) % total));
  const goToIndex = (index) => withPause(() => setCurrent(index));

  const handleTouchStart = (event) => {
    if (!isMobile || useArrowNavigation || total <= 1) return;

    touchStartXRef.current = event.touches[0].clientX;
    touchStartYRef.current = event.touches[0].clientY;
    touchEndXRef.current = event.touches[0].clientX;
    touchEndYRef.current = event.touches[0].clientY;
  };

  const handleTouchMove = (event) => {
    if (!isMobile || useArrowNavigation || total <= 1) return;

    touchEndXRef.current = event.touches[0].clientX;
    touchEndYRef.current = event.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!isMobile || useArrowNavigation || total <= 1) return;

    const deltaX = touchStartXRef.current - touchEndXRef.current;
    const deltaY = Math.abs(touchStartYRef.current - touchEndYRef.current);
    const minSwipeDistance = 42;

    if (Math.abs(deltaX) > minSwipeDistance && Math.abs(deltaX) > deltaY) {
      if (deltaX > 0) {
        goNext();
      } else {
        goPrev();
      }
    }

    touchStartXRef.current = 0;
    touchStartYRef.current = 0;
    touchEndXRef.current = 0;
    touchEndYRef.current = 0;
  };

  if (!cards.length) return null;

  return (
    <section
      ref={sectionRef}
      className={`${styles.tipologias} ${isVisible ? styles["tipologias--visible"] : ""}`}
    >
      {data?.title && <h2 className={styles.tipologias__title}>{data.title}</h2>}

      <div className={styles.tipologias__carousel}>
        {useArrowNavigation && (
          <button
            type="button"
            className={`${styles.tipologias__arrow} ${styles["tipologias__arrow--left"]}`}
            onClick={goPrev}
            aria-label={getUiText(locale, 'previousTypology')}
          >
            <img src={iconFlechaIzq} alt={getUiText(locale, 'previousTypology')} title={getUiText(locale, 'previousTypology')} aria-hidden="true" className={styles.tipologias__arrowIcon} />
          </button>
        )}

        <div
          className={styles.tipologias__track}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {cards.map((card, index) => {
            const imageMedia = getStrapiMediaAttributes(card?.image, {
              altFallback: card?.title || data?.title || "",
              titleFallback: card?.title || data?.title || "",
            });
            if (!imageMedia.src) return null;

            const relative = index - current;
            const wrappedRelative = relative > total / 2
              ? relative - total
              : relative < -total / 2
                ? relative + total
                : relative;
            const isFocused = wrappedRelative === 0;
            const isNeighbor = Math.abs(wrappedRelative) === 1;
            const slotClass = isFocused
              ? styles["tipologias__card--focused"]
              : wrappedRelative === -1
                ? styles["tipologias__card--left"]
                : wrappedRelative === 1
                  ? styles["tipologias__card--right"]
                  : wrappedRelative < 0
                    ? styles["tipologias__card--hiddenLeft"]
                    : styles["tipologias__card--hiddenRight"];

            const cardClass = [
              styles.tipologias__card,
              slotClass,
              isNeighbor ? styles["tipologias__card--neighbor"] : null,
            ].filter(Boolean).join(" ");

            const displayPrice = mostrarPrecio
              ? (() => {
                  const cardPriceLabel = card?.priceLabel?.trim();
                  if (cardPriceLabel) {
                    const numericPrice = toNumericPriceValue(cardPriceLabel);
                    if (Number.isFinite(numericPrice) && numericPrice > 0) {
                      return formatPriceWithPrefix(cardPriceLabel, companyInfo, locale);
                    }

                    return formatPriceText(cardPriceLabel, companyInfo, locale);
                  }

                  const cardSlug = getSlugFromUrl(card?.ctaUrl);
                  const categoryPrice = cardSlug ? priceMap?.[cardSlug] : null;
                  return formatPriceWithPrefix(categoryPrice, companyInfo, locale);
                })()
              : null;

            return (
              <article
                key={card.id || index}
                className={cardClass}
                onClick={() => {
                  if (!isFocused) {
                    goToIndex(index);
                  }
                }}
              >
                <img src={imageMedia.src} alt={imageMedia.alt} title={imageMedia.title} className={styles.tipologias__image} />
                <div className={styles.tipologias__overlay} />

                <div className={styles.tipologias__content} style={{position: 'absolute', bottom: '22px'}}>
                  {card?.title && <h3 className={styles.tipologias__cardTitle}>{card.title}</h3>}

                  {displayPrice && (
                    <p className={styles.tipologias__price}>{displayPrice}</p>
                  )}

                  {!!card?.fields?.length && (
                    <ul className={styles.tipologias__fields}>
                      {card.fields.map((field, fieldIndex) => {
                        const iconMedia = field?.icon?.url
                          ? getStrapiMediaAttributes(field.icon, {
                            altFallback: getIconFallbackLabel(locale, 'typology', field.label),
                            titleFallback: getIconFallbackLabel(locale, 'typology', field.label),
                          })
                          : null;
                        if (!field?.label) return null;

                        return (
                          <li key={field.id || fieldIndex} className={styles.tipologias__field}>
                            {iconMedia?.src && (
                              <img
                                src={iconMedia.src}
                                alt={iconMedia.alt}
                                title={iconMedia.title}
                                className={styles.tipologias__fieldIcon}
                                aria-hidden="true"
                              />
                            )}
                            <span>{field.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {card?.ctaLabel && (
                    <a
                      href={card?.ctaUrl || "/?contacto"}
                      className={`${styles.tipologias__cta} ${!isFocused ? styles["tipologias__cta--hidden"] : ""}`}
                      title={card.ctaLabel}
                    >
                      {card.ctaLabel}
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {useArrowNavigation && (
          <button
            type="button"
            className={`${styles.tipologias__arrow} ${styles["tipologias__arrow--right"]}`}
            onClick={goNext}
            aria-label={getUiText(locale, 'nextTypology')}
          >
            <img src={iconFlechaDer} alt={getUiText(locale, 'nextTypology')} title={getUiText(locale, 'nextTypology')} aria-hidden="true" className={styles.tipologias__arrowIcon} />
          </button>
        )}
      </div>

      {showDots && (
        <div className={styles.tipologias__dots} aria-label={getUiText(locale, 'typologyNavigation')}>
          {cards.map((card, index) => {
            const isActive = index === current;
            return (
              <button
                key={card.id || index}
                type="button"
                className={`${styles.tipologias__dot} ${isActive ? styles["tipologias__dot--active"] : ""}`}
                onClick={() => {
                  if (!isActive) {
                    goToIndex(index);
                  }
                }}
                aria-label={getUiText(locale, 'goToTypology', { index: index + 1 })}
                aria-pressed={isActive}
              />
            );
          })}
        </div>
      )}
    </section>
  );
};

export default Tipologias;
